import {
  ApiError,
  errorMessage,
  haversineDistanceM,
  PEAK_CHECKIN_RADIUS_M,
  SUMMIT_CHECKIN_REFUSAL_CODES,
  type SummitCheckinRefusalCode,
} from "@atlas/shared";
import { useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Camera, ImagePlus, Lock, LockOpen, Trash2, X } from "lucide-react";
import {
  fetchCheckinUploadUrl,
  fetchLandmarkCheckins,
  fetchPeakCheckins,
  patchCheckin,
  postLandmarkCheckin,
  postPeakCheckin,
  removeCheckin,
  sendCheckinReport,
  type CreateCheckinResponse,
  type LandmarkCheckinsResponse,
} from "../api/landmarks";
import { readExifCapture, type ExifCapture } from "../lib/exif";
import { getStampAsset } from "../lib/cosmetics";
import { AtlasPanel } from "./AtlasPanel";
import { Button } from "./Button";
import { Skel } from "./Skeleton";
import { EmptyState } from "./EmptyState";

// Photo check-in dialog + gallery, shared by the landmark page (LMK-11) and
// the peak page (summit check-ins, spec 34).

const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const CAPTION_MAX = 280;

function readImageDimensions(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("Could not read image"));
    img.src = url;
  });
}

// ─── Check-in dialog (LMK-11) ─────────────────────────────────────────────────

/**
 * What a check-in is for. A summit check-in needs the peak's coordinates so
 * the photo's location can be checked before anything is uploaded.
 */
export type CheckinTarget =
  | { kind: "landmark"; slug: string }
  | { kind: "peak"; slug: string; name: string; latitude: number; longitude: number };

const SUMMIT_REFUSAL_MESSAGES: Record<SummitCheckinRefusalCode, string> = {
  no_location:
    "This photo has no location saved in it, so it can't show you were at the top. Try the original photo from your camera roll.",
  too_far: "This photo wasn't taken at the summit. Summit check-ins need a photo taken at the top.",
  history_locked:
    "The free plan records the last 12 months. Full history unlocks older summits.",
};

function summitRefusal(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 422) return null;
  const code = err.body?.["code"];
  return typeof code === "string" && (SUMMIT_CHECKIN_REFUSAL_CODES as readonly string[]).includes(code)
    ? SUMMIT_REFUSAL_MESSAGES[code as SummitCheckinRefusalCode]
    : null;
}

export function CheckinDialog({
  target,
  onClose,
  onSuccess,
}: {
  target: CheckinTarget;
  onClose: () => void;
  onSuccess: (result: CreateCheckinResponse) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Summit check-ins only: the photo's own location, read on selection so a
  // photo that can't count is refused before it is uploaded.
  const [exif, setExif] = useState<ExifCapture | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSummit = target.kind === "peak";

  async function selectFile(f: File) {
    if (!ALLOWED_PHOTO_TYPES.includes(f.type)) {
      setError("Photos must be JPEG, PNG or WebP");
      return;
    }
    if (f.size > MAX_PHOTO_BYTES) {
      setError("Photo must be 5 MB or smaller");
      return;
    }
    setError(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setExif(null);
    if (target.kind === "peak") {
      const capture = await readExifCapture(f);
      setExif(capture);
      if (capture.lat === null || capture.lon === null) {
        setError(SUMMIT_REFUSAL_MESSAGES.no_location);
      } else {
        const d = haversineDistanceM(capture.lat, capture.lon, target.latitude, target.longitude);
        if (d > PEAK_CHECKIN_RADIUS_M) {
          setError(
            `This photo was taken ${d >= 1000 ? `${(d / 1000).toFixed(1)} km` : `${Math.round(d)} m`} from the summit of ${target.name}. Summit check-ins need a photo taken at the top.`,
          );
        }
      }
    }
  }

  // A summit photo must have been read and passed before it can be sent.
  const summitBlocked = isSummit && (exif === null || error !== null);

  async function submit() {
    if (!file || !previewUrl || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const capture = exif ?? (await readExifCapture(file));
      const [{ width, height }, { uploadUrl, key }] = await Promise.all([
        readImageDimensions(previewUrl),
        fetchCheckinUploadUrl(file.type),
      ]);
      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!uploadRes.ok) throw new Error("Photo upload failed");

      const body = {
        clientId: crypto.randomUUID(),
        photoKey: key,
        width,
        height,
        caption: caption.trim() || undefined,
        checkedInAt: (capture.takenAt ?? new Date()).toISOString(),
        exifLat: capture.lat ?? undefined,
        exifLon: capture.lon ?? undefined,
      };
      const result =
        target.kind === "peak"
          ? await postPeakCheckin(target.slug, body)
          : await postLandmarkCheckin(target.slug, body);
      URL.revokeObjectURL(previewUrl);
      onSuccess(result);
    } catch (err) {
      setError(summitRefusal(err) ?? errorMessage(err, "Check-in failed"));
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-md)",
          width: "min(440px, 100%)",
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 16, color: "var(--text-primary)" }}>
            {isSummit ? "Check in at the summit" : "Check in with photo"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", padding: 4 }}
          >
            <X size={18} />
          </button>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_PHOTO_TYPES.join(",")}
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void selectFile(f);
          }}
        />

        {previewUrl ? (
          <div style={{ position: "relative" }}>
            <img
              src={previewUrl}
              alt="Check-in preview"
              style={{ width: "100%", maxHeight: 280, objectFit: "cover", borderRadius: "var(--radius-md)", display: "block" }}
            />
            <Button variant="secondary" size="sm" style={{ position: "absolute", right: 10, bottom: 10 }} onClick={() => inputRef.current?.click()}>
              Change photo
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              padding: "36px 16px",
              borderRadius: "var(--radius-md)",
              border: "1px dashed var(--border-default)",
              background: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              fontFamily: "var(--font-sans)",
              fontSize: 13.5,
            }}
          >
            <ImagePlus size={22} />
            {isSummit ? "Choose a photo taken at the top" : "Choose a photo taken at the landmark"}
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {isSummit
                ? `It needs its location saved, within ${PEAK_CHECKIN_RADIUS_M} m of the summit`
                : "JPEG, PNG or WebP · up to 5 MB"}
            </span>
          </button>
        )}

        <div>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, CAPTION_MAX))}
            placeholder="Add a caption (optional)"
            rows={2}
            style={{
              width: "100%",
              resize: "vertical",
              padding: "10px 12px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border-default)",
              background: "var(--surface-sunken, transparent)",
              color: "var(--text-primary)",
              fontFamily: "var(--font-sans)",
              fontSize: 13.5,
              boxSizing: "border-box",
            }}
          />
          <div className="tnum" style={{ fontSize: 11.5, color: "var(--text-muted)", textAlign: "right" }}>
            {caption.length} / {CAPTION_MAX}
          </div>
        </div>

        {error && (
          <div style={{ fontSize: 13, color: "var(--coral-500, #e5484d)" }}>{error}</div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={!file || submitting || summitBlocked}>
            {submitting ? "Checking in…" : "Check in"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Gallery panel (LMK-11) ───────────────────────────────────────────────────

/** Gallery query key per target, so the pages can invalidate it after a check-in. */
export function checkinGalleryKey(target: { kind: "landmark" | "peak"; slug: string }): string[] {
  return target.kind === "peak" ? ["peaks", target.slug, "checkins"] : ["landmarks", target.slug, "checkins"];
}

export function CheckinGallery({
  target,
  isAuthenticated,
}: {
  target: { kind: "landmark" | "peak"; slug: string };
  isAuthenticated: boolean;
}) {
  const queryClient = useQueryClient();
  const galleryKey = checkinGalleryKey(target);
  const slug = target.slug;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: galleryKey,
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      target.kind === "peak" ? fetchPeakCheckins(slug, pageParam) : fetchLandmarkCheckins(slug, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: LandmarkCheckinsResponse) => last.nextCursor ?? undefined,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: galleryKey });

  const privacyMutation = useMutation({
    mutationFn: ({ id, isPrivate }: { id: string; isPrivate: boolean }) => patchCheckin(id, { isPrivate }),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => removeCheckin(id),
    onSuccess: invalidate,
  });
  const reportMutation = useMutation({
    mutationFn: (id: string) => sendCheckinReport(id),
  });

  const checkins = data?.pages.flatMap((p) => p.checkins) ?? [];

  return (
    <AtlasPanel title="Recent check-ins">
      {isLoading ? (
        <Skel height="140px" />
      ) : checkins.length === 0 ? (
        <EmptyState
          icon={<Camera size={18} />}
          title="No check-ins yet"
          hint={
            target.kind === "peak"
              ? "Photos from explorers who check in at the top will appear in this gallery."
              : "Photos from explorers who check in here will appear in this gallery."
          }
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            {checkins.map((ci) => (
              <figure key={ci.id} style={{ margin: 0, minWidth: 0 }}>
                <div style={{ position: "relative" }}>
                  <img
                    src={ci.photoUrl}
                    alt={ci.caption ?? (target.kind === "peak" ? "Summit check-in photo" : "Landmark check-in photo")}
                    loading="lazy"
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      borderRadius: "var(--radius-md)",
                      display: "block",
                      border: "1px solid var(--border-subtle)",
                      opacity: ci.isPrivate || ci.moderationStatus === "hidden" ? 0.6 : 1,
                    }}
                  />
                  {ci.stamp && getStampAsset(ci.stamp.assetKey) && (
                    <img
                      src={getStampAsset(ci.stamp.assetKey)}
                      alt=""
                      title={ci.stamp.name}
                      style={{
                        position: "absolute",
                        bottom: 6,
                        left: 6,
                        width: 44,
                        height: 44,
                        pointerEvents: "none",
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))",
                      }}
                    />
                  )}
                  {ci.isOwn && (
                    <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 6 }}>
                      <button
                        type="button"
                        title={ci.isPrivate ? "Private - only you can see this. Click to make public." : "Public. Click to make private."}
                        onClick={() => privacyMutation.mutate({ id: ci.id, isPrivate: !ci.isPrivate })}
                        style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)", borderRadius: 8, padding: 5, cursor: "pointer", color: "var(--text-secondary)", display: "flex" }}
                      >
                        {ci.isPrivate ? <Lock size={13} /> : <LockOpen size={13} />}
                      </button>
                      <button
                        type="button"
                        title="Delete this check-in (your visit still counts)"
                        onClick={() => {
                          if (window.confirm("Delete this check-in? The photo is removed but your visit still counts.")) {
                            deleteMutation.mutate(ci.id);
                          }
                        }}
                        style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)", borderRadius: 8, padding: 5, cursor: "pointer", color: "var(--text-secondary)", display: "flex" }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
                <figcaption style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 2 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    {ci.explorer.avatarUrl && (
                      <img src={ci.explorer.avatarUrl} alt="" style={{ width: 16, height: 16, borderRadius: "50%", objectFit: "cover" }} />
                    )}
                    {ci.explorer.profilePublic && ci.explorer.username ? (
                      <Link
                        to="/explorers/$username"
                        params={{ username: ci.explorer.username }}
                        style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        @{ci.explorer.username}
                      </Link>
                    ) : (
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {ci.explorer.username ? `@${ci.explorer.username}` : "Explorer"}
                      </span>
                    )}
                    <span style={{ fontSize: 11.5, color: "var(--text-muted)", marginLeft: "auto", whiteSpace: "nowrap" }}>
                      {new Date(ci.checkedInAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                    </span>
                  </div>
                  {ci.caption && (
                    <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.5 }}>{ci.caption}</div>
                  )}
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {ci.isOwn && ci.isPrivate && (
                      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Private</span>
                    )}
                    {ci.isOwn && ci.moderationStatus === "hidden" && (
                      <span style={{ fontSize: 11, color: "var(--coral-500, #e5484d)" }}>Hidden by moderation</span>
                    )}
                    {!ci.isOwn && isAuthenticated && (
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm("Report this photo to the Atlas team?")) reportMutation.mutate(ci.id);
                        }}
                        style={{ background: "none", border: "none", padding: 0, fontSize: 11, color: "var(--text-muted)", cursor: "pointer", fontFamily: "var(--font-sans)" }}
                      >
                        Report
                      </button>
                    )}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
          {hasNextPage && (
            <Button variant="secondary" size="sm" style={{ alignSelf: "center" }} onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
              {isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          )}
        </div>
      )}
    </AtlasPanel>
  );
}

